import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, Skeleton, Collapse } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { aiApi } from '../../services/api';

interface AIInsightChipProps {
  context: string;
  maxInsights?: number;
}

const AIInsightChip: React.FC<AIInsightChipProps> = ({ context, maxInsights = 3 }) => {
  const [insights, setInsights] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    aiApi.getInsights(context)
      .then(data => {
        if (!cancelled) {
          setInsights(data.insights.slice(0, maxInsights + 2));
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [context, maxInsights]);

  if (error || (!loading && insights.length === 0)) return null;

  return (
    <Box sx={{
      mb: 2,
      borderRadius: '6px',
      border: '1px solid',
      // R4: surface migrated from old-gold tint to paprika-soft (AI insights live in
      // the paprika family per brand-system.md). Border uses a slightly stronger paprika
      // alpha to delineate against the lifted-dark paper.
      borderColor: 'var(--paprika-soft)',
      bgcolor: 'var(--paprika-soft)',
      overflow: 'hidden',
    }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'var(--paprika-soft)' },
        }}
        onClick={() => setExpanded(prev => !prev)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon sx={{ fontSize: 16, color: 'var(--paprika-on-soft)' }} />
          {/* R4 Cat 2: section header reads as a plain heading; brand emphasis comes from
              the surrounding paprika tint, not the title color. */}
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'text.primary' }}>
            AI Insights
          </Typography>
          {!loading && (
            <Chip
              label={`${insights.length}`}
              size="small"
              sx={{ height: 18, fontSize: '10.5px', bgcolor: 'var(--paprika-soft)', color: 'var(--paprika-on-soft)' }}
            />
          )}
        </Box>
        {expanded ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 1.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              <Skeleton variant="text" width="90%" height={20} />
              <Skeleton variant="text" width="80%" height={20} />
              <Skeleton variant="text" width="85%" height={20} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              {insights.slice(0, expanded ? undefined : maxInsights).map((insight, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Typography sx={{ fontSize: '11px', color: 'var(--paprika-on-soft)', mt: 0.2, lineHeight: 1 }}>
                    {i + 1}.
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.5 }}>
                    {insight}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AIInsightChip;
